import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { Loader } from 'components/Loader';

import {
    useGetProductsToOrderOnceForSupplyOrderQuery,
    useGetSupplyOrderDetailsQuery,
    useSupplyOrderDetailsUpdatedOrCreatedSubscription,
} from 'data/__generated__';

import { SupplyOrderDetailsForm } from './SupplyOrderDetailsForm';

type ParamTypes = {
    supplyOrderId: string;
};

/**
 * Point d'entrée de la fiche d'achat : une seule requête bloquante, et légère (un `getById` plus trois
 * dataloaders). Le catalogue fournisseur et les prévisions ont leurs propres requêtes, déclenchées à
 * l'ouverture de leur section.
 */
export const SupplyOrderDetails = () => {
    const { supplyOrderId = '' } = useParams<ParamTypes>();

    const { data, loading, error } = useGetSupplyOrderDetailsQuery({ variables: { supplyOrderId } });

    // Les produits « à commander une fois » ne servent qu'à marquer des lignes du tableau : leur absence
    // ne doit pas retenir l'affichage de l'achat.
    const { data: productsToOrderOnceData } = useGetProductsToOrderOnceForSupplyOrderQuery({
        variables: { supplyOrderId },
    });

    // Quelqu'un d'autre modifie l'achat → Apollo normalise l'entité poussée et la fiche se réaligne.
    // Pas d'écriture de cache manuelle : l'identifiant est dans la charge utile, le cache sait où aller.
    useSupplyOrderDetailsUpdatedOrCreatedSubscription({
        onData: ({ data: { data: subscriptionData } }) => {
            const hasThisOrder = subscriptionData?.supplyOrdersUpdatedOrCreated.some(
                ({ _id }) => _id === supplyOrderId,
            );
            if (hasThisOrder) {
                toast.info("L'achat vient d'être modifié : la fiche a été mise à jour.");
            }
        },
    });

    if (loading) {
        return (
            <CenteredContainer>
                <Loader />
            </CenteredContainer>
        );
    }

    // Un id inconnu ou malformé (lien collé à la main) n'est pas un bug : la page le dit au lieu de tomber.
    if (!data?.supplyOrder) {
        return (
            <CenteredContainer>
                <Missing data-test="supply-order-not-found">
                    {error?.networkError ? "L'achat n'a pas pu être chargé, réessayez." : 'Achat introuvable'}
                </Missing>
            </CenteredContainer>
        );
    }

    return (
        <SupplyOrderDetailsForm
            supplyOrder={data.supplyOrder}
            productsToOrderOnce={productsToOrderOnceData?.productsToOrderOnceForSupplyOrder ?? []}
        />
    );
};

const CenteredContainer = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Missing = styled.div`
    padding: 30px;
    text-align: center;
    color: ${({ theme }) => theme.infoTextColor};
`;
